import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import { AuthShell } from "@/components/shared/AuthShell";
import { useNeutralAuthCover } from "@/hooks/useNeutralAuthCover";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { PasswordStrengthIndicator } from "@/components/shared/PasswordStrengthIndicator";
import { useAuth } from "@/contexts/AuthContext";
import { setPassword as setPasswordApi } from "@/services/authService";

/**
 * Forced first-time password setup. Reached automatically after a root user
 * accepts an invite — they have no password yet and ProtectedRoute pushes
 * them here until one is set.
 */
export function SetInitialPasswordPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isLoading, refreshUser } = useAuth();
  const cover = useNeutralAuthCover();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isLoading && user != null && user.hasPassword) {
    return <Navigate to="/" replace />;
  }
  if (!isLoading && user == null) {
    return <Navigate to="/login" replace />;
  }

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error(
        t("auth.setInitialPassword.tooShort", "Password must be at least 8 characters."),
      );
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(t("auth.setInitialPassword.mismatch", "Passwords do not match."));
      return;
    }
    setIsSubmitting(true);
    try {
      await setPasswordApi({ newPassword });
      await refreshUser();
      toast.success(
        t("auth.setInitialPassword.success", "Password set. You're all set!"),
      );
      navigate("/", { replace: true });
    } catch {
      toast.error(
        t(
          "auth.setInitialPassword.error",
          "Could not save the password. Try again.",
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthShell cover={cover}>
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-bold text-foreground">
            {t("auth.setInitialPassword.title", "Set your password")}
          </h1>
          <p className="text-balance text-muted-foreground">
            {t(
              "auth.setInitialPassword.subtitle",
              "Choose a password to finish setting up your account. You'll use it to sign in next time.",
            )}
          </p>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="grid gap-2">
            <Label htmlFor="newPassword">
              {t("auth.setInitialPassword.newPassword", "New password")}
            </Label>
            <PasswordInput
              id="newPassword"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              minLength={8}
              required
            />
            <PasswordStrengthIndicator
              password={newPassword}
              showRules
              rulesLabels={{
                minLength: t("auth.register.passwordStrength.minLength"),
                uppercase: t("auth.register.passwordStrength.uppercase"),
                number: t("auth.register.passwordStrength.number"),
                special: t("auth.register.passwordStrength.special"),
              }}
              strengthLabels={{
                weak: t("auth.register.passwordStrength.weak"),
                fair: t("auth.register.passwordStrength.fair"),
                good: t("auth.register.passwordStrength.good"),
                strong: t("auth.register.passwordStrength.strong"),
              }}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="confirmPassword">
              {t("auth.setInitialPassword.confirmPassword", "Confirm password")}
            </Label>
            <PasswordInput
              id="confirmPassword"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              minLength={8}
              required
            />
          </div>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting
              ? t("auth.setInitialPassword.submitting", "Saving…")
              : t("auth.setInitialPassword.submit", "Save password")}
          </Button>
        </form>
      </div>
    </AuthShell>
  );
}
